/**
 * Better Auth's own routes, reached through the same door as everything else.
 *
 * `/api/auth/*` is Better Auth's default base path and the API mounts it there, so the
 * client needs no configuration beyond the origin — which the Vite proxy makes
 * same-origin, so the session cookie is first-party. See `client.ts`'s header for why
 * that is a proxy's job and not CORS's.
 *
 * This file lives beside the tRPC client rather than anywhere else for the reason
 * `src/api/` exists at all: it is the second thing in the SPA that talks to the network,
 * and the eslint rule banning `fetch(` outside this directory would otherwise have an
 * exception on its first day.
 */
import { createAuthClient } from "better-auth/react";

export const authClient = createAuthClient({
  // Same origin, through the proxy. Not read from an environment variable: the SPA is a
  // static build served from the same host as the API in every environment that exists
  // (c39's Caddy in production, Vite's proxy in development), so a configurable origin
  // would be a knob with one legal value and a way to point a login form somewhere else.
  baseURL: window.location.origin,
});

export type SignInResult = Awaited<ReturnType<typeof authClient.signIn.email>>;

/**
 * The message a failed sign-in or sign-up should show.
 *
 * Better Auth answers with its own error shape rather than an RFC 9457 body — it owns
 * authentication and `PROBLEMS` owns everything after it (U1's split) — so this is the
 * one place in the SPA that reads a second error vocabulary, and it says so rather than
 * pretending the two are one.
 *
 * The fallback is deliberately not "something went wrong": a sign-in that fails without
 * a reason is nearly always the credentials, and saying so is more useful than a shrug.
 */
export function authErrorMessage(
  error: { message?: string | undefined; code?: string | undefined } | null | undefined,
): string {
  // The signup allowlist refuses with this code deliberately, so it now means "off-list
  // OR already registered" and Better Auth's own wording is false in the first case.
  // Matched on the code, not the message, so a rewording upstream cannot silently
  // restore the falsehood. — apps/api/src/auth/instance.ts
  if (error?.code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL") {
    return (
      "This address can't be used to sign up. If you were invited, check it against " +
      "your invitation; if you already have an account, sign in instead."
    );
  }
  const message = error?.message;
  return message === undefined || message === ""
    ? "That email and password did not match. Check them and try again."
    : message;
}
